import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Download, Pencil, Trash2, MessageSquareOff, BellOff, AlertTriangle, Users, ScrollText, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { syncFromUser } from "@/lib/consent";

const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function PrivacyCentrePage() {
  const { user, updateUser, logout } = useAuth();
  const [me, setMe] = useState(null);
  const load = () => api.get("/privacy/me").then((r) => setMe(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!me) return <div className="p-8 text-slate-400">Loading your privacy settings…</div>;

  return (
    <div data-testid="privacy-centre" className="mx-auto max-w-4xl px-4 py-8 text-white sm:px-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-cyan-400">Account</p>
      <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Privacy & Data</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Your rights under India's Digital Personal Data Protection Act, 2023 — access, correct, delete your data, and change consent — without digging through the policy. Grievance contact: <a className="text-cyan-400 underline" href={`mailto:${me.grievance_email}`}>{me.grievance_email}</a>.</p>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <Card icon={ShieldCheck} title="My personal data" testid="card-my-data">
          <Row k="Name" v={user.name} /><Row k="Email" v={user.email} /><Row k="Mobile" v={user.mobile || "—"} />
          <Row k="Date of birth" v={me.dob || "not provided"} /><Row k="Age status" v={me.age_status} />
          <Row k="Privacy Notice accepted" v={me.notice_accepted_version ? `v${me.notice_accepted_version}` : "pending"} />
          {me.age_status === "child" && <Row k="Guardian consent" v={me.guardian_consent?.status || "pending"} />}
          <Row k="AI coach messages stored" v={me.chat_message_count} />
        </Card>

        <ConsentCard me={me} onSaved={(state) => { updateUser({ consent_state: state }); syncFromUser({ ...user, consent_state: state }); load(); }} />

        <Card icon={Download} title="Download / access my data" testid="card-export">
          <p className="text-xs text-slate-400">Get a machine-readable JSON copy of everything we hold about you: profile, consents, AI chats, assessment, orders, bookings and notifications.</p>
          <button data-testid="export-data-btn" onClick={async () => {
            try { const { data } = await api.get("/privacy/export"); dl(data, `entrancezone-ai-data-${Date.now()}.json`); toast.success("Your data export has been downloaded"); load(); }
            catch (e) { toast.error(err(e)); }
          }} className="mt-3 rounded-lg bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016]">Download my data (JSON)</button>
        </Card>

        <CorrectionCard user={user} me={me} onSaved={(patch) => { updateUser(patch); load(); }} />

        <Card icon={MessageSquareOff} title="AI chat history" testid="card-chat">
          <p className="text-xs text-slate-400">{me.chat_message_count} messages stored. Deleting removes them from our database immediately; the AI provider keeps no copy beyond its short abuse-monitoring window.</p>
          <button data-testid="delete-all-chats-btn" onClick={async () => {
            if (!window.confirm("Delete ALL your AI coach conversations? This cannot be undone.")) return;
            try { const { data } = await api.delete("/privacy/chat"); toast.success(`Deleted ${data.deleted} messages`); load(); } catch (e) { toast.error(err(e)); }
          }} className="mt-3 rounded-lg border border-rose-400/40 px-4 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-400/10">Delete all chat history</button>
          <Link to="/chat" className="ml-3 text-xs text-cyan-400 underline">Delete individual chats →</Link>
        </Card>

        <GrievanceCard me={me} onSaved={load} />

        <Card icon={ScrollText} title="Consent history (audit ledger)" testid="card-ledger">
          <ul className="max-h-40 space-y-1 overflow-y-auto text-[11px] text-slate-400">
            {me.ledger.map((c) => <li key={c._id || c.at + c.consent_type}>{c.at?.slice(0, 16).replace("T", " ")} · <b className="text-slate-200">{c.consent_type}</b> → {c.status} (notice v{c.notice_version}, via {c.source})</li>)}
            {!me.ledger.length && <li>No consent records yet.</li>}
          </ul>
        </Card>

        <EraseCard user={user} logout={logout} />
      </div>
    </div>
  );
}

const ConsentCard = ({ me, onSaved }) => {
  const [state, setState] = useState(me.consent_state);
  const [busy, setBusy] = useState(false);
  const child = me.age_status === "child";
  const optional = Object.entries(me.consent_types).filter(([, v]) => !v.required);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/privacy/consents", { consents: state }); setState(data.consent_state); onSaved(data.consent_state); toast.success("Consent preferences updated"); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <Card icon={BellOff} title="Consent & communication preferences" testid="card-consents">
      {child && <p className="mb-2 rounded-lg bg-amber-400/10 p-2 text-[11px] text-amber-200">Marketing and tracking are permanently off for under-18 accounts.</p>}
      <div className="space-y-2">
        {optional.map(([k, v]) => (
          <label key={k} className="flex items-start gap-2 text-xs text-slate-300">
            <input data-testid={`consent-${k}-checkbox`} type="checkbox" disabled={child} checked={!!state[k]} onChange={(e) => setState({ ...state, [k]: e.target.checked })} className="mt-0.5 accent-cyan-400" />
            <span><b className="text-white">{v.label}</b><br /><span className="text-slate-500">{v.purpose}</span></span>
          </label>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Service messages (receipts, OTPs, security alerts) are not affected. Withdrawing stops future processing for that purpose immediately.</p>
      <button data-testid="save-consents-btn" onClick={save} disabled={busy || child} className="mt-3 rounded-lg bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] disabled:opacity-50">{busy ? "Saving…" : "Save preferences"}</button>
    </Card>
  );
};

const CorrectionCard = ({ user, me, onSaved }) => {
  const [name, setName] = useState(user.name || "");
  const [mobile, setMobile] = useState(user.mobile || "");
  const [dob, setDob] = useState("");
  const [note, setNote] = useState("");
  const submit = async () => {
    try {
      const { data } = await api.post("/privacy/correction", { name, mobile, dob: !me.dob && dob ? dob : undefined, note });
      toast.success(data.updated.length ? `Updated: ${data.updated.join(", ")}` : "Correction request logged for review"); onSaved({ name, mobile: mobile || null });
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <Card icon={Pencil} title="Correct my data" testid="card-correction">
      <div className="grid gap-2 sm:grid-cols-2">
        <Inp testid="correct-name-input" label="Name" value={name} onChange={setName} />
        <Inp testid="correct-mobile-input" label="Mobile" value={mobile} onChange={setMobile} placeholder="+91…" />
        {!me.dob && <Inp testid="correct-dob-input" label="Date of birth" type="date" value={dob} onChange={setDob} />}
        <Inp testid="correct-note-input" label="Other correction (we'll review)" value={note} onChange={setNote} placeholder="e.g. wrong email" />
      </div>
      <button data-testid="save-correction-btn" onClick={submit} className="mt-3 rounded-lg bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016]">Save corrections</button>
    </Card>
  );
};

const GrievanceCard = ({ me, onSaved }) => {
  const [category, setCategory] = useState("consent");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/privacy/grievances", { category, description: desc }); toast.success(`Grievance ${data.ticket_id} raised — we aim to respond within ${data.target_days} days`); setDesc(""); onSaved(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <Card icon={AlertTriangle} title="Raise a privacy grievance" testid="card-grievance">
      <select data-testid="grievance-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#0B0F17] px-3 py-2 text-xs text-white">
        {["consent", "access_request", "correction", "erasure", "marketing", "ai_processing", "security", "other"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
      </select>
      <textarea data-testid="grievance-description-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Describe the issue (min 10 characters)"
        className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white" />
      <button data-testid="submit-grievance-btn" onClick={submit} disabled={busy || desc.trim().length < 10} className="mt-2 rounded-lg bg-cyan-400 px-4 py-2 text-xs font-bold text-[#041016] disabled:opacity-50">Submit grievance</button>
      {me.grievances.length > 0 && <ul className="mt-3 space-y-1 text-[11px] text-slate-400">{me.grievances.map((g) => <li key={g.ticket_id} data-testid="grievance-row"><b className="text-slate-200">{g.ticket_id}</b> · {g.category} · {g.status}{g.response ? ` — ${g.response}` : ""}</li>)}</ul>}
    </Card>
  );
};

const EraseCard = ({ user, logout }) => {
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const erase = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/privacy/erasure", { confirm, password: password || undefined });
      setResult(data); toast.success("Your account has been erased"); setTimeout(() => logout(), 4000);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  if (result) return (
    <Card icon={Trash2} title="Account erased" testid="card-erase-done">
      <p className="text-xs text-slate-300">Deleted: {Object.entries(result.deleted).map(([k, v]) => `${k} (${v})`).join(", ")}.</p>
      <p className="mt-2 text-xs text-slate-400">Retained under legal/business necessity (pseudonymised, no name/email): {Object.keys(result.retained).join(", ")}. Signing you out…</p>
    </Card>
  );
  return (
    <Card icon={Trash2} title="Delete account / request erasure" testid="card-erase" danger>
      <p className="text-xs text-slate-400">Permanently deletes your profile, assessment, AI chats, notifications and links; anonymises forum posts. Payment records are kept only as required for accounting/tax (with no name or email) and your consent history is retained as compliance evidence. Active subscriptions end without refund unless our Refund Policy applies.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <Inp testid="erase-confirm-input" label='Type DELETE to confirm' value={confirm} onChange={setConfirm} />
        {user.auth_provider !== "google" && <Inp testid="erase-password-input" label="Your password" type="password" value={password} onChange={setPassword} />}
      </div>
      <button data-testid="erase-account-btn" onClick={erase} disabled={busy || confirm !== "DELETE"} className="mt-3 rounded-lg bg-rose-500 px-4 py-2 text-xs font-bold text-white disabled:opacity-40">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete my account permanently"}</button>
    </Card>
  );
};

const Card = ({ icon: Icon, title, children, testid, danger }) => (
  <section data-testid={testid} className={`rounded-2xl border p-5 ${danger ? "border-rose-400/20 bg-rose-400/[0.03]" : "border-white/10 bg-white/[0.02]"}`}>
    <h2 className="flex items-center gap-2 text-sm font-bold"><Icon className={`h-4 w-4 ${danger ? "text-rose-400" : "text-cyan-400"}`} /> {title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);
const Row = ({ k, v }) => <div className="flex justify-between gap-4 border-b border-white/5 py-1.5 text-xs"><span className="text-slate-500">{k}</span><span className="text-right text-slate-200">{String(v)}</span></div>;
const Inp = ({ testid, label, value, onChange, type = "text", placeholder }) => (
  <label className="text-[11px] text-slate-500">{label}
    <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white" /></label>
);
const dl = (obj, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" })); a.download = name; a.click(); };
export { Users };
